import { BookOpen } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

// User-facing rendering of the data-processing and consent notice,
// drawn from the counsel-review draft (Sections 3 and 5 through 9).
// Plain language, no internal scaffolding. Counsel finalizes wording
// and we record the version each person agrees to.
const NOTICE_SECTIONS = [
  {
    title: "What we collect",
    body: "We collect only what these purposes need, and nothing more.",
    points: [
      "The answers you give Klair during the conversation, in your own words.",
      "The read the engine builds from those answers: your interests, strengths, values, direction, and readiness.",
      "Your account basics, such as name, age band, email, and, if you joined through a college, the college name.",
      "Basic technical data needed to run the service securely, such as device and session information.",
    ],
  },
  {
    title: "Why we collect it",
    body: "Each purpose is specific. We do not use your data for a new purpose without asking you again.",
    points: [
      "To run your assessment conversation and build your private career read.",
      "To show your roadmap, your prediction card, your progress over time, and your companion.",
      "To let you build a resume and a shareable one-pager if you choose to.",
      "To keep the service secure and to meet legal and audit duties.",
    ],
  },
  {
    title: "The legal basis",
    body: "Your consent, given by a clear affirmative action. For anyone under eighteen, the verifiable consent of a parent or lawful guardian. Some limited processing may rest on other lawful grounds the Act allows, such as security and legal compliance.",
  },
  {
    title: "Children and guardians",
    body: "If you are under eighteen, you never reach the agree button on your own. We route you to a guardian sign-off first, and the assessment does not start until a verified parent or lawful guardian consents. We never process a child's data in a way likely to harm their wellbeing, we run no advertising, and the dark side and derailer layer never runs for a minor.",
  },
  {
    title: "Your rights",
    body: "You can reach all of these from your account under data and privacy.",
    points: [
      "See a plain summary of your data and how it is processed.",
      "Correct and complete your record.",
      "Delete your data.",
      "Raise a grievance through a named contact and a defined response window.",
      "Nominate someone to act for you if you cannot.",
      "Withdraw your consent as easily as you gave it.",
    ],
  },
  {
    title: "Withdraw, and how to reach us",
    body: "You can withdraw your consent at any time from your account settings, as easily as you gave it, and withdrawing it stops any further processing. For any question or request about your data, contact the Data Protection contact named in this notice. If you are not satisfied, you may complain to the Data Protection Board of India.",
  },
  {
    title: "Keeping and deleting your data",
    body: "We keep your data only as long as a stated purpose needs it, and we erase it when the purpose is served or you withdraw consent, subject to any retention the law requires. If your account stays inactive for a long time, we give you advance notice before we erase, and the clock is shorter and tighter on the minor path.",
  },
  {
    title: "Security and breaches",
    body: "We hold your data under reasonable safeguards: encryption in transit and at rest, least privilege access, a wall that keeps any one person off every group view, and audit logging the writing service cannot edit. If a breach affects you, we tell you without delay in plain language on your registered channel, and we notify the Data Protection Board within the window the Rules set.",
  },
  {
    title: "Our promises, beyond the law",
    points: [
      "Only you ever see you. No college, employer, or partner ever gets your profile, your answers, your score, or your report. They only ever see group patterns, and small groups are hidden.",
      "Klareet is a tool to help you grow, never a tool to rank or pick people. It never sends a rank, hire, or promote signal about you.",
      "We do not sell your personal data to anyone, for any purpose.",
      "Your wellbeing comes before the product. Distress is never scored, never shown to a buyer, and always routed to support.",
      "During the build, the engine runs on clearly labelled synthetic and trial data, and no real person is scored for a real decision until a licensed panel signs the real instrument and its norms.",
    ],
  },
];

export function ConsentNoticeDialog({ version }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="k-btn k-btn-ghost"
          data-testid="consent-full-notice-open"
        >
          <BookOpen size={16} /> Read the full notice
        </button>
      </DialogTrigger>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="consent-full-notice-modal"
      >
        <DialogHeader>
          <DialogTitle className="klareet-serif text-2xl">
            How Klareet uses your information
          </DialogTitle>
          <DialogDescription className="text-sm" style={{ color: "hsl(var(--klareet-ink-soft))" }}>
            Klareet asks you questions to understand your interests, strengths,
            values, and direction, and returns a private career read, a roadmap,
            and an ongoing companion. That is personal data processing, and in
            India it is governed by the Digital Personal Data Protection Act 2023
            and its 2025 Rules. Here is the full detail of what we collect, why,
            and the rights you have.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-5">
          {NOTICE_SECTIONS.map((s) => (
            <section key={s.title} data-testid={`notice-section-${s.title.toLowerCase().replaceAll(" ", "-").replaceAll(",", "")}`}>
              <h4 className="klareet-serif text-base font-semibold mb-1" style={{ color: "hsl(var(--klareet-ink))" }}>
                {s.title}
              </h4>
              {s.body && (
                <p className="text-sm" style={{ color: "hsl(var(--klareet-ink-soft))" }}>{s.body}</p>
              )}
              {s.points && (
                <ul className="mt-2 space-y-1.5">
                  {s.points.map((p, i) => (
                    <li key={i} className="flex gap-2 text-sm" style={{ color: "hsl(var(--klareet-ink-soft))" }}>
                      <span aria-hidden="true" style={{ color: "hsl(var(--klareet-teal))" }}>&bull;</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <div
          className="mt-4 pt-4 text-xs"
          style={{ borderTop: "1px solid hsl(var(--klareet-line-soft))", color: "hsl(var(--klareet-muted))" }}
        >
          We record which version of this notice you agree to, and when. If the
          wording changes in a way that matters, we ask you again rather than
          update it quietly.
          {version ? (
            <>
              {" "}Current version <span className="klareet-mono">{version}</span>.
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
